import React, {
  useCallback,
  useContext,
  useEffect, useLayoutEffect, useMemo, useRef, useState
} from 'react';
import ReactDOM from 'react-dom';
import { DndContext } from 'react-dnd';
import './index.css';

import {
  CharacterMetadata,
  ContentBlock,
  ContentState,
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  convertFromRaw,
} from 'draft-js';
import { useStore } from '../../store/store';

// common components
import '../common/Utils.css';

import { getBackgroundBorderStyle, mergeConfigurationColors, mergeConfigurationHighlightColors, mergeConfigurationTextStyles, calculateOptimalLineHeight } from '../../utils/styles';
import _ from 'lodash';
import TextStylingComponent, { imposedInlineStyles } from '../TextStylingComponent/TextStylingComponent'
import { useBulletDraftJs, useStyleMap } from '../../hooks';
import { decorator, getBlockStyle, handleBeforeInput, handleBoxDrop, splitContentAtSelection } from './utils';
import { useStyledPaste } from './useStyledPaste';
import EditorWrapper from '../common/EditorWrapper';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import { MENU_ANCESTORS, viewerTypes } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import { EditorProvider } from '../../contexts/EditorContext';
import { patchBoxContent } from '../../collab/useEditorCollab';
import { isAVariableBox } from '../../utils/boxes';
import { documentEditorKey, registerActiveField } from '../../utils/activeFields';
import { hydrateVariables } from '../../utils/predefinedValues';
import { leftShownStatus, useOverlay } from '../../contexts/OverlayContext';
import FloatyTextStyling from '../Table/FloatyTextStyling';
import BoxActionsPanel from '../common/BoxActionsPanel';
import { useTabHandler } from '../../hooks';
import shallow from 'zustand/shallow';

export const setDefaultInlineStyleIfNeeded = (
  currentEditorState,
  removedProperty,
  defaultProperty,
  prefix = ''
) => {
  let nextContentState = currentEditorState.getCurrentContent();
  const blocks = [];
  nextContentState.getBlockMap().forEach((block) => {
    const characterList = block.getCharacterList().map((character) => {
      if (character.hasStyle(`${prefix}${removedProperty}`)) {
        return CharacterMetadata.removeStyle(
          character,
          `${prefix}${removedProperty}`
        );
      } else {
        return character;
      }
    });
    blocks.push(
      new ContentBlock({
        key: block.getKey(),
        text: block.getText(),
        type: block.getType(),
        depth: block.getDepth(),
        data: block.getData(),
        characterList,
      })
    );
  });

  return EditorState.push(
    currentEditorState,
    ContentState.createFromBlockArray(blocks),
    'change-inline-style'
  );
};

// Store selector
const selector = (state) => {
  return {
    removeBox: state.removeBox,
    updateBox: state.updateBox,
    configuration: state.configuration,
    setBoxMovable: state.setBoxMovable,
    setEventManagerEnabled: state.setEventManagerEnabled,
    fromPdf: state.fromPdf,
    variables: state.variables,
    currentDocument: state.currentDocument,
    maskEditing: state.maskEditing,
    tmp: state.tmp,
    zoomLevel: state.zoomLevel,
    landscape: state.landscape,
  };
};

const RichEditor = React.memo(({ box, drawMode, isSelected }) => {
  const styleMap = useStyleMap()
  const editorRef = useRef(null);
  const boundingBoxRef = useRef(null);
  const { updateLeftShownStatus, overlayProps } = useOverlay();
  const shownStatus = overlayProps?.left?.shownStatus
  const {
    updateBox: storeUpdateBox,
    setBoxMovable,
    configuration,
    setEventManagerEnabled,
    fromPdf,
    variables,
    currentDocument,
    maskEditing,
    tmp,
    zoomLevel,
    landscape,
  } = useStore(selector, shallow);

  const updateBox = useMemo(() => box.updateBox ?? storeUpdateBox, [box.updateBox, storeUpdateBox]);

  const textStyle = useMemo(
    () => mergeConfigurationTextStyles(configuration, tmp).find((ts) => ts.id === box?.textStyleId) || configuration.textStyles[0],
    [configuration, tmp, box?.textStyleId]
  );

  // Rendu du texte hors style posé sur la sélection, tel que le cadre de la box
  // l'applique (cf. son style plus bas) : le style « Défaut » prend police,
  // taille et couleur des paramètres du document. La barre de mise en forme
  // s'en sert pour montrer ce qui est appliqué.
  const renderedTextDefaults = useMemo(() => {
    const fromDocument = textStyle.id === 0;
    return {
      defaultFontSize: fromDocument ? configuration.fontSize : textStyle.fontSize,
      defaultFontFamily: fromDocument ? configuration.fontFamily : textStyle.fontFamily,
      defaultColor: fromDocument ? configuration.baseFontColor : textStyle.color,
      defaultInlineStyles: imposedInlineStyles(textStyle),
    };
  }, [configuration.fontSize, configuration.fontFamily, configuration.baseFontColor, textStyle]);
  const alignment = box.content.alignment || textStyle.positions.horizontal;

  const colors = useMemo(
    () => mergeConfigurationColors(configuration, tmp),
    [configuration, tmp]
  );

  const [editorState, setEditorState] = useState(EditorState.createWithContent(
    box.content.editorState.getCurrentContent(),
    decorator
  ));

  const [defaultSelection, setDefaultSelection] = useState(null);
  const fixCaretAfterFirstInputRef = useRef(false);

  useBulletDraftJs();

  const saveBoxImmediate = useCallback((editorState) => {
    if (boundingBoxRef.current) {
      updateBox(box.id, (box) => {
        const nextContent = editorState.getCurrentContent();
        // Contenu inchangé (l'utilisateur n'a pas tapé, ContentState partagée) :
        // ne PAS forger un nouvel editorState — sinon nouvelle ref de box à
        // chaque déselect = diff fantôme capturé par le do/undo (viderait le
        // redo). On laisse aussi la box telle quelle (pas de save inutile).
        if (box.content.editorState.getCurrentContent() === nextContent) return;
        if (!box.isBoxSimulated)
          box.height = boundingBoxRef?.current.firstChild.clientHeight;
        box.content.editorState = EditorState.createWithContent(
          nextContent,
          decorator
        );
      });
    }
  }, [box?.id, updateBox]);

  const write = useCallback((incomingEditorState) => {
    patchBoxContent(box.id, incomingEditorState);
    setEditorState((prevEditorState) => {
      const prevWasEmpty = !prevEditorState.getCurrentContent().hasText();
      const nextHasText = incomingEditorState.getCurrentContent().hasText();

      if (prevWasEmpty && nextHasText) {
        fixCaretAfterFirstInputRef.current = true;
        return EditorState.moveFocusToEnd(incomingEditorState);
      }
      return incomingEditorState;
    });
  }, [box.id]);

  // Écriture du menu « @ » / « / » : il passe une fonction de mise à jour pour
  // lire l'état courant. Même envoi que la frappe, sinon la variable insérée
  // n'atteint le brouillon collab (et la base) qu'à la désélection de la box.
  const writeFromMenu = useCallback((next) => {
    setEditorState((prevEditorState) => {
      const resolved = typeof next === 'function' ? next(prevEditorState) : next;
      if (resolved !== prevEditorState) patchBoxContent(box.id, resolved);
      return resolved;
    });
  }, [box.id]);

  // État courant lisible hors rendu : l'insertion d'une valeur prédéfinie est
  // déclenchée par un bouton extérieur à l'éditeur.
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  // Insertion d'une valeur prédéfinie à la position du curseur. Le bouton qui
  // la déclenche est rendu dans le libellé de la ligne du formulaire, hors de
  // cet éditeur : seul l'éditeur connaît sa sélection courante.
  // Restreint à l'aperçu du formulaire, sinon l'éditeur du document et celui de
  // l'aperçu se disputeraient la même clé.
  useEffect(() => {
    if (box.fromViewer !== viewerTypes.VARIABLES) return undefined;
    return registerActiveField(box.id, {
      insertRawContent: (rawContent) => {
        let fragment;
        try {
          // Le contenu inséré porte le texte figé à la saisie de la valeur,
          // soit le nom des variables : il faut le traduire avec les valeurs
          // du document, sinon le nom resterait affiché jusqu'au prochain
          // chargement.
          const { variables: storeVariables, currentDocument } = useStore.getState();
          const hydrated = hydrateVariables({
            editorState: EditorState.createWithContent(
              convertFromRaw(JSON.parse(rawContent)),
              decorator
            ),
            variables: storeVariables,
            isTemplate: currentDocument.type === 'template',
          });
          fragment = hydrated.getCurrentContent().getBlockMap();
        } catch (e) {
          // Valeur prédéfinie illisible : ne rien insérer plutôt que casser.
          return;
        }
        const previous = editorStateRef.current;
        const contentAfter = Modifier.replaceWithFragment(
          previous.getCurrentContent(),
          previous.getSelection(),
          fragment
        );
        // Écrit aussi dans le store : la box peut ne pas être sélectionnée,
        // l'état local ne serait alors jamais sauvegardé. Même ContentState des
        // deux côtés, la sauvegarde à la déselection devient un no-op.
        updateBox(box.id, (b) => {
          b.content.editorState = EditorState.createWithContent(contentAfter, decorator);
        });
        setEditorState(
          EditorState.forceSelection(
            EditorState.push(previous, contentAfter, 'insert-fragment'),
            contentAfter.getSelectionAfter()
          )
        );
      },
    });
  }, [box.id, box.fromViewer, updateBox]);

  // Box en cours d'édition : son état local ne relit plus le store (cf. l'effet
  // sur !isSelected) et l'écrase à la désélection. Une lecture ou une
  // modification venue d'ailleurs (affichage d'une variable adresse, panel
  // droit) passe donc par lui, sinon elle verrait un contenu périmé ou serait
  // perdue.
  useEffect(() => {
    if (box.fromViewer || !isSelected) return undefined;
    return registerActiveField(documentEditorKey(box.id), {
      getEditorState: () => editorStateRef.current,
      // `transform` reçoit l'état courant et rend le nouveau ContentState, ou
      // null pour ne rien changer.
      replaceContent: (transform) => {
        const previous = editorStateRef.current;
        const contentAfter = transform(previous);
        if (!contentAfter) return;
        const next = EditorState.push(previous, contentAfter, 'apply-entity');
        // Lu par le panel au rendu que déclenche updateBox, avant que
        // l'éditeur ne se re-rende.
        editorStateRef.current = next;
        // Même ContentState dans le store : la sauvegarde à la désélection
        // devient un no-op.
        updateBox(box.id, (b) => {
          b.content.editorState = EditorState.createWithContent(contentAfter, decorator);
        });
        patchBoxContent(box.id, next);
        setEditorState(next);
      },
    });
  }, [box.id, box.fromViewer, isSelected, updateBox]);

  const handleKeyCommand = useCallback(
    (command) => {
      if (command) {
        setEditorState(
          RichUtils.toggleInlineStyle(editorState, command.toUpperCase())
        );
      }
    },
    [editorState]
  );

  const { myKeyBindingFn } = useTabHandler(editorState, setEditorState);

  // Pas de DndProvider dans tous les rendus (useDragDropManager lèverait).
  const dragDropManager = useContext(DndContext).dragDropManager;
  const handleDrop = useMemo(() => handleBoxDrop(dragDropManager?.getMonitor()), [dragDropManager]);

  const highlightColors = useMemo(
    () => mergeConfigurationHighlightColors(configuration, tmp),
    [configuration, tmp]
  );
  const styledPaste = useStyledPaste({
    editorState,
    write,
    anchorRef: boundingBoxRef,
    active: isSelected,
    colors,
    highlightColors,
    defaultColor: renderedTextDefaults.defaultColor,
    defaultFontSize: renderedTextDefaults.defaultFontSize,
  });

  const splitTextBoxAtCaret = useStore((state) => state.splitTextBoxAtCaret);

  // Entrée : ce qui suit le curseur part dans une nouvelle box texte créée en
  // dessous, qui prend le focus. Ctrl/Cmd/Maj + Entrée gardent le retour ligne.
  //
  // Écarté :
  // - listes à puces, où Entrée doit poursuivre la liste puis en sortir sur un
  //   item vide (comportement par défaut de draft-js) ;
  // - mode dessin, masques, aperçu et formulaire de variables : pas de flux de
  //   colonne où insérer quoi que ce soit ;
  // - tout refus du store (verrou, section liée) → retour ligne classique.
  const handleReturn = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return 'not-handled';
      if (drawMode || box.fromViewer || box.isBoxSimulated || box.maskId)
        return 'not-handled';

      const blockType = RichUtils.getCurrentBlockType(editorState);
      if (
        blockType === 'unordered-list-item' ||
        blockType === 'ordered-list-item'
      )
        return 'not-handled';

      const { head, tail } = splitContentAtSelection(editorState);
      const headEditorState = EditorState.createWithContent(head, decorator);
      const created = splitTextBoxAtCaret({
        boxId: box.id,
        headEditorState,
        tailEditorState: tail
          ? EditorState.createWithContent(tail, decorator)
          : null,
      });
      if (!created) return 'not-handled';

      // Évite d'afficher le texte entier le temps que la déselection propage le
      // contenu tronqué du store.
      setEditorState(headEditorState);
      return 'handled';
    },
    [
      box.id,
      box.fromViewer,
      box.isBoxSimulated,
      box.maskId,
      drawMode,
      editorState,
      splitTextBoxAtCaret,
    ]
  );

  // Focus + curseur en fin de bloc (évite offset 0 sur bloc vide)
  useEffect(() => {
    if (!drawMode && isSelected) {
      setEditorState((prev) => EditorState.moveFocusToEnd(prev));
      requestAnimationFrame(() => editorRef.current?.focus());
    }
  }, [drawMode, isSelected]);

  // Après la 1re lettre sur bloc vide, EditorWrapper peut remettre le caret au début
  useLayoutEffect(() => {
    if (!fixCaretAfterFirstInputRef.current || !isSelected) return;
    fixCaretAfterFirstInputRef.current = false;

    const content = editorState.getCurrentContent();
    if (!content.hasText()) return;

    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const blockLength = content.getBlockForKey(blockKey).getLength();

    if (
      selection.getAnchorOffset() === blockLength &&
      selection.getFocusOffset() === blockLength
    ) {
      requestAnimationFrame(() => editorRef.current?.focus());
      return;
    }

    setEditorState(EditorState.forceSelection(
      editorState,
      selection.merge({
        anchorOffset: blockLength,
        focusOffset: blockLength,
        hasFocus: true,
      })
    ));
    requestAnimationFrame(() => editorRef.current?.focus());
  }, [editorState, isSelected]);

  // Empêcher le scroll automatique lors de la saisie avec zoom < 100%
  useEffect(() => {
    // Ne s'exécuter que si on est en mode template ou en mode édition de masque
    const isTemplateMode = currentDocument?.type === 'template';
    const isMaskEditingMode = maskEditing && maskEditing.length > 0;
    if (!isTemplateMode && !isMaskEditingMode) return;

    if (!isSelected || !editorRef.current) return;

    // Vérifier si le zoom est différent de 100%
    const currentZoom = effectiveZoom(zoomLevel, landscape);
    if (currentZoom === 100) return; // Pas de problème à 100%

    const editorElement = editorRef.current;

    // Trouver le conteneur de scroll Virtuoso
    const findScrollContainer = () => {
      let element = editorElement;
      let attempts = 0;
      const maxAttempts = 50; // Limiter les tentatives pour éviter les boucles infinies
      while (element && element !== document.body && attempts < maxAttempts) {
        if (element.classList && element.classList.contains('react-virtuoso-scroller')) {
          return element;
        }
        element = element.parentElement;
        attempts++;
      }
      return null;
    };

    const scrollContainer = findScrollContainer();
    if (!scrollContainer) return;

    let scrollPosition = null;
    let isTyping = false;
    let rafId = null;

    const saveScrollPosition = () => {
      scrollPosition = scrollContainer.scrollTop;
    };

    const restoreScrollPosition = () => {
      if (scrollPosition !== null && isTyping) {
        scrollContainer.scrollTop = scrollPosition;
      }
    };

    const handleBeforeInput = () => {
      if (!isTyping) {
        saveScrollPosition();
        isTyping = true;
      }
    };

    const handleInput = () => {
      if (isTyping && scrollPosition !== null) {
        // Annuler toute animation frame précédente
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        // Restaurer la position après que le navigateur ait tenté de scroller
        rafId = requestAnimationFrame(() => {
          restoreScrollPosition();
          rafId = requestAnimationFrame(() => {
            restoreScrollPosition();
          });
        });
      }
    };

    const handleScroll = () => {
      if (isTyping && scrollPosition !== null) {
        // Si le scroll a changé de manière significative pendant la saisie, le restaurer
        const currentScroll = scrollContainer.scrollTop;
        const diff = Math.abs(currentScroll - scrollPosition);
        if (diff > 2) {
          // Restaurer immédiatement
          scrollContainer.scrollTop = scrollPosition;
        }
      }
    };

    const handleBlur = () => {
      isTyping = false;
      scrollPosition = null;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // Écouter les événements
    editorElement.addEventListener('beforeinput', handleBeforeInput);
    editorElement.addEventListener('input', handleInput);
    editorElement.addEventListener('blur', handleBlur);
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      editorElement.removeEventListener('beforeinput', handleBeforeInput);
      editorElement.removeEventListener('input', handleInput);
      editorElement.removeEventListener('blur', handleBlur);
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isSelected, zoomLevel, landscape, currentDocument?.type, maskEditing]);
  const cleanupRef = useRef(editorState);
  const consumeSkipBoxSaveOnUnselect = useStore((state) => state.consumeSkipBoxSaveOnUnselect);
  const documentContentSyncTick = useStore((state) => state.documentContentSyncTick);

  useEffect(() => {
    cleanupRef.current = editorState
  });

  useLayoutEffect(() => {
    return () => {
      if (isSelected && cleanupRef.current) {
        if (consumeSkipBoxSaveOnUnselect(box.id)) return;
        saveBoxImmediate(cleanupRef.current);
      }
    };
  }, [isSelected, saveBoxImmediate, box.id, consumeSkipBoxSaveOnUnselect]);

  useEffect(() => {
    if (!isSelected) {
      setEditorState(EditorState.createWithContent(
        box.content.editorState.getCurrentContent(),
        decorator
      ))
    }
  }, [box?.content?.editorState, isSelected])

  useEffect(() => {
    setEditorState(EditorState.createWithContent(
      box.content.editorState.getCurrentContent(),
      decorator
    ));
  }, [documentContentSyncTick, box.id])

  const placeholder = useMemo(() => {
    const bulletStatus = RichUtils.getCurrentBlockType(editorState)
    return fromPdf || box.fromViewer === viewerTypes.PREVIEW ||
      bulletStatus === "unordered-list-item" ||
      bulletStatus === "ordered-list-item" ?
      "" : 'Votre texte ici'
  }, [box.fromViewer, editorState, fromPdf])

  const menuAncestorIds = useMemo(() => [box.id], [box.id])

  const applyStyleTextVariable = useCallback((style) => {
    if ((box.backgroundColor?.label === "transparent" && box?.border?.color?.label === "transparent") ||
      (_.isEmpty(box?.border) && _.isEmpty(box?.backgroundColor))) {
      return textStyle[style]
    } else {
      return box[style]
    }
  }, [textStyle, box])

  return (
    <EditorProvider setEditorState={setEditorState} editorState={editorState} readOnly={!isSelected} boxId={box.id}>
      <div
        ref={boundingBoxRef}
        id={box.id}
        style={{
          ...getBackgroundBorderStyle(
            applyStyleTextVariable("backgroundColor"),
            applyStyleTextVariable("border")
          ),
          width: box.width,
          fontFamily: textStyle.id === 0 ? undefined : textStyle.fontFamily,
          fontSize: textStyle.id === 0 ? undefined : textStyle.fontSize,
          color: textStyle.id === 0 ? configuration.baseFontColor.style : textStyle.color.style,
          fontWeight: textStyle.fontWeight,
          fontStyle: textStyle.fontStyle,
          textDecoration: textStyle.textDecoration,
          lineHeight: calculateOptimalLineHeight(textStyle.id === 0 ? configuration.fontSize : textStyle.fontSize),
        }}
        onClick={() => {
          if (isSelected && !editorState.getSelection().getHasFocus()) {
            editorRef.current.focus();
          }
        }}
        onDoubleClick={(event) => {
          if (isAVariableBox(box?.type) && !box.isBoxSimulated) {
            updateLeftShownStatus(leftShownStatus["VARIABLES"], box.id)
          }
          event.stopPropagation()
        }}
        onKeyDown={(e) => {
          styledPaste.onKeyDown(e);
          if (editorState.getSelection().getHasFocus())
            e.stopPropagation()
        }}
        onKeyUp={styledPaste.onKeyUp}
      >
        <div style={{
          padding: `${textStyle.paddingTopBottom}px ${textStyle.paddinngLeftRight}px`
        }}>
          <EditorWrapper boxId={box.id} editorState={editorState} setEditorState={setEditorState} box={box} textStyle={textStyle}>
            <Editor
              stripPastedStyles={true}
              handlePastedText={styledPaste.handlePastedText}
              handleDrop={handleDrop}
              handleBeforeInput={(chars, editorState) => handleBeforeInput(
                {
                  chars,
                  editorState,
                  setEditorState
                }
              )}
              placeholder={placeholder}
              blockStyleFn={getBlockStyle}
              className='RichEditor-editor'
              style={{
                overflow: 'visible',
              }}
              customStyleMap={styleMap}
              editorState={editorState}
              handleKeyCommand={handleKeyCommand}
              handleReturn={handleReturn}
              onFocus={(event) => {
                setBoxMovable(false);
                setEventManagerEnabled(false);
              }}
              onChange={write}
              keyBindingFn={myKeyBindingFn}
              spellCheck={true}
              textAlignment={alignment === 'justify' ? 'left' : alignment}
              ref={editorRef}
              readOnly={!isSelected}
            />
          </EditorWrapper>
        </div>
        {styledPaste.pasteChoice}
      </div>
      {isSelected &&
        ReactDOM.createPortal(
          <ContextualMenu
            editorState={editorState}
            setEditorState={writeFromMenu}
            menuAncestorType={MENU_ANCESTORS.BOX.content}
            menuAncestorIds={menuAncestorIds}
            defaultSelection={defaultSelection}
            setDefaultSelection={setDefaultSelection}
          />,
          document.getElementById('SelectionVariablePortal')
        )}
      {isSelected && !box.isBoxSimulated && (shownStatus !== "variables" || box.fromViewer === viewerTypes.VARIABLES) && (
        <>
          <TextStylingComponent
            box={box}
            updateBox={updateBox}
            editorState={editorState}
            setEditorState={setEditorState}
            variables={variables}
            colors={colors}
            textStyle={textStyle}
            boxId={box.id}
            hasAlignement
            updateBoxOnStylingChange={() => null}
            setDefaultSelection={setDefaultSelection}
            hasForm
            hasVariables
            {...renderedTextDefaults}
            boxAlignement={alignment}
            wrap
          />
          <BoxActionsPanel />
          {ReactDOM.createPortal(
            <FloatyTextStyling
              editorState={editorState}
              setEditorState={setEditorState}
              {...renderedTextDefaults}
              boxId={box.id}
              updateBox={updateBox}
              hasAlignement
              boxAlignement={alignment}
              // Pas de textStyle : le panneau (TextStylingComponent wrap
              // ci-dessus) a déjà son sélecteur de style, la barre en ajoutait
              // un second, sans titre, en bas du panneau.
              colors={colors}
            />,
            document.getElementById('SelectionVariablePortal')
          )}
        </>
      )}
      {isSelected && box.isBoxSimulated &&
        ReactDOM.createPortal(
          <FloatyTextStyling
            editorState={editorState}
            setEditorState={setEditorState}
            {...renderedTextDefaults}
            boxId={box.id}
            updateBox={updateBox}
            hasAlignement={box.isBoxSimulated}
            boxAlignement={alignment}
            textStyle={box.withMenu ? textStyle : undefined}
            colors={colors}
          />,
          document.getElementById('SelectionVariablePortal')
        )
      }
    </EditorProvider>
  );
});

export default RichEditor;
